import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import awsConfig from './aws-config';

// Context & Auth
import { AuthProvider } from './context/AuthContext';
import { AuthModalWrapper } from './components/auth';

// Layout
import { Header } from './components/layout';

// Config
import { styles } from './config/styles';
import { color, gradient, radius } from './config/tokens';
import CookieConsent from './components/common/CookieConsent';
import { openCookieSettings } from './utils/analytics';

// Lazy-loaded pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const DonatePage = lazy(() => import('./pages/DonatePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ToolPage = lazy(() => import('./pages/ToolPage'));
const DiscussionPage = lazy(() => import('./pages/DiscussionPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      color: color.text.muted
    }}>
      <div style={{ textAlign: 'center' }}>
        <div
          className="animate-spin"
          style={{
            width: '32px',
            height: '32px',
            border: `3px solid ${color.border.default}`,
            borderTopColor: color.accent.primary,
            borderRadius: radius.round,
            margin: '0 auto 1rem'
          }}
          aria-hidden="true"
        />
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
}

// Error fallback UI
function ErrorFallback({ error, resetError }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: color.bg.base,
      padding: '2rem'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '500px'
      }}>
        <h1 style={{ color: color.text.heading, fontSize: '1.5rem', marginBottom: '1rem' }}>
          Something went wrong
        </h1>
        <p style={{ color: color.text.muted, marginBottom: '1.5rem' }}>
          We've been notified and are looking into it. Please try again.
        </p>
        <button
          onClick={resetError}
          style={{
            padding: '0.75rem 1.5rem',
            background: gradient.primary,
            color: 'white',
            border: 'none',
            borderRadius: radius.md,
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

// Initialize AWS Amplify
Amplify.configure(awsConfig);

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();

  return (
    <div style={styles.container}>
      {/* Skip link for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <Header currentPath={location.pathname} />

      <main id="main-content" style={styles.main} tabIndex={-1}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/donate" element={<DonatePage />} />
            <Route path="/discussion" element={<DiscussionPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/tools/:toolId" element={<ToolPage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
        </Suspense>
      </main>

      <footer style={{
        borderTop: `1px solid ${color.border.subtle}`,
        marginTop: '3rem',
        padding: '1.5rem 0',
        textAlign: 'center',
        color: color.text.faint,
        fontSize: '0.875rem'
      }}>
        <div>A Bee Line Microsystems LLC Research Project</div>
        <div style={{ marginTop: '0.5rem' }}>
          Powered by the <a href="https://brighteon.ai" target="_blank" rel="noopener noreferrer" style={{ color: color.text.subtle, textDecoration: 'underline' }}>Enoch</a> model from Brighteon.ai
        </div>
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/privacy" style={{ color: color.text.subtle, textDecoration: 'underline' }}>Privacy</Link>
          <Link to="/terms" style={{ color: color.text.subtle, textDecoration: 'underline' }}>Terms</Link>
          <button
            onClick={openCookieSettings}
            style={{ background: 'none', border: 'none', padding: 0, color: color.text.subtle, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}
          >
            Cookie preferences
          </button>
        </div>
      </footer>

      <AuthModalWrapper />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
